/**
 * Netly Pay domain rules.
 *
 * A port of `docs/design/netly-pay/netlypay-data.js` from the design handoff,
 * whose README says to bring this logic to the real service layer. Every
 * derived figure a Netly Pay screen shows comes from here, so a tile, a table
 * and a drawer cannot disagree about what a member owes.
 *
 * Two deliberate departures from the prototype:
 *
 * 1. **Money is in minor units** (cents), because that is what the Pay API
 *    sends and takes: `amountMinor`, `balanceMinor`. The prototype used whole
 *    rupees. The arithmetic below is unit-agnostic — it adds and subtracts
 *    whatever it is given — but `money()` divides by 100, so a figure that
 *    reaches it in major units renders 100× too small. Amounts entering this
 *    module come from the API, or from `toMinor()`.
 *
 * 2. **Today is a parameter.** The prototype froze `TODAY = '2026-10-20'`. A
 *    request is overdue relative to the real date, and a dashboard left open
 *    overnight must not keep yesterday's answer.
 *
 * See docs/netly-pay-integration.md and docs/design/netly-pay/DESIGN-BRIEF.md
 * ("Domain rules").
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Types ───────────────────────────────────────────────────────────────────

/** A line on a payment request. */
export interface RequestItem {
  label: string;
  amountMinor: number;
}

export interface RequestPayment {
  /** ISO date the money was taken, not when it was recorded. */
  date: string;
  amountMinor: number;
  method: string;
  ref?: string;
  /** 'POS' for money taken at a till — see CONTRACT-NETLISE.md §3. */
  source?: string;
  posReceiptNo?: string;
}

/**
 * Stored request states. Anything else is DERIVED from the money and the due
 * date, which is why `status` is not a field.
 */
export type RequestState = 'draft' | 'cancelled' | 'waiting' | null;

export type RequestStatus = 'draft' | 'cancelled' | 'waiting' | 'paid' | 'partly' | 'overdue' | 'due';

export interface PaymentRequest {
  id: string;
  /** null for a draft addressed to a batch or a program. */
  memberId: string | null;
  recipients?: {mode: 'member' | 'batch' | 'program'; ids: string[]};
  title: string;
  items: RequestItem[];
  issued: string;
  due: string;
  state: RequestState;
  payments?: RequestPayment[];
  /** A credit note reduces the total; it is not a payment. */
  creditMinor?: number;
  recurring?: boolean;
  lateFeeMinor?: number | null;
  /** How long after the due date the late fee lands. Paired with the amount. */
  lateFeeDays?: number | null;
  partAllowed?: boolean;
  /** The smallest instalment, when part payment is allowed. */
  partMinMinor?: number | null;
  note?: string;
  reminders?: string[];
  createdBy?: string;
  /** Why the credit note was issued, and when — both shown on the timeline. */
  creditReason?: string;
  creditAt?: string;
  cancelledAt?: string;
  /** Proofs staff turned down, newest last. */
  rejections?: {at: string; reason: string}[];
}

/** Stored member states. 'overdue' and 'active' are derived, never stored. */
export type MemberState = 'frozen' | 'expired' | 'pending' | null;

export type MemberStatus = 'frozen' | 'expired' | 'pending' | 'overdue' | 'active';

export interface Guardian {
  name: string;
  relation: string;
  phone: string;
}

export interface Member {
  id: string;
  name: string;
  batchId: string;
  phone: string;
  email?: string;
  dob?: string;
  joined: string;
  state: MemberState;
  guardians: Guardian[];
  custom?: Record<string, string>;
  /** The linked POS customer (D57). Never set automatically. */
  posCustomerId?: string | null;
  freezeNote?: string;
  merged?: string[];
}

export interface Batch {
  id: string;
  programId: string;
  name: string;
  coach: string;
  venue: string;
  capacity: number;
  feeMinor: number;
  slots: {day: number; start: string; end: string}[];
}

export interface Program {
  id: string;
  name: string;
  desc?: string;
  tone?: string;
}

export interface Session {
  id: string;
  batchId: string;
  date: string;
  start: string;
  end: string;
}

/** 'P' present · 'L' late · 'A' absent · 'E' excused. */
export type AttendanceMark = 'P' | 'L' | 'A' | 'E';

/** What a screen needs in order to derive anything. */
export interface NetlyPayData {
  programs: Program[];
  batches: Batch[];
  members: Member[];
  requests: PaymentRequest[];
  approvals?: Approval[];
  sessions?: Session[];
  /** sessionId → memberId → mark. */
  attendance?: Record<string, Record<string, AttendanceMark>>;
}

export interface Approval {
  id: string;
  requestId: string;
  memberId: string;
  amountMinor: number;
  method: string;
  ref?: string;
  paidOn: string;
  submitted: string;
  proof?: string;
  /** Set when another proof pays the same request. */
  dupOf?: string | null;
}

// ── Dates ───────────────────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Today as an ISO date in the browser's own timezone, not UTC. */
export function todayISO(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** `15 Oct 2026`. */
export function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d} ${MONTHS[+m - 1]} ${y}`;
}

/** `15 Oct` — for axis labels and dense tables. */
export function fmtShort(iso?: string | null): string {
  if (!iso) return '—';
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d} ${MONTHS[+m - 1]}`;
}

/** `15 Oct 2026, 19:42`. Drops the time when there is none. */
export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return fmtDate(iso) + (iso.length > 10 ? `, ${iso.slice(11, 16)}` : '');
}

export function addDays(iso: string, n: number): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Whole days from `a` to `b`; negative when `b` is earlier. */
export function diffDays(a: string, b: string): number {
  const ms =
    new Date(`${b.slice(0, 10)}T00:00:00`).getTime() - new Date(`${a.slice(0, 10)}T00:00:00`).getTime();
  return Math.round(ms / 864e5);
}

export function weekday(iso: string): number {
  return new Date(`${iso.slice(0, 10)}T00:00:00`).getDay();
}

/** The Monday of that week — the collections chart is bucketed by it. */
export function weekStart(iso: string): string {
  return addDays(iso, -((weekday(iso) + 6) % 7));
}

// ── Money ───────────────────────────────────────────────────────────────────

/**
 * Major units to minor: 8000 → 800000.
 *
 * `Math.round(n * 100)` alone is not enough: `1.005 * 100` is
 * 100.49999999999999 in binary floating point, so a fee typed as 1.005 would
 * round DOWN to 100. The nudge is one epsilon of the scaled value, which lifts
 * a number that only just missed the halfway point without moving one that is
 * genuinely below it.
 */
export function toMinor(major: number | string): number {
  const n = Number(major);
  if (!Number.isFinite(n)) return 0;
  const scaled = n * 100;
  return Math.round(scaled + Math.sign(scaled) * Number.EPSILON * Math.abs(scaled));
}

/**
 * `Rs. 8,000.00`, with a true minus sign for negatives (expenses in the
 * ledger). Non-numbers render as an em dash, never as `NaN`.
 */
export function money(minor?: number | null, currency = 'Rs.'): string {
  if (minor == null || !Number.isFinite(Number(minor))) return '—';
  const n = Number(minor) / 100;
  const s = Math.abs(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  return `${n < 0 ? '−' : ''}${currency} ${s}`;
}

/** `Rs. 8.5k` — for chart labels, where two decimals are noise. */
export function moneyShort(minor?: number | null, currency = 'Rs.'): string {
  if (minor == null || !Number.isFinite(Number(minor))) return '—';
  const major = Number(minor) / 100;
  if (Math.abs(major) >= 1000) return `${currency} ${Math.round(major / 100) / 10}k`;
  return `${currency} ${major}`;
}

// ── Phones ──────────────────────────────────────────────────────────────────

/** Just the digits, for comparing `077 123 4501` with `+94771234501`. */
export function digitsOf(phone?: string | null): string {
  return String(phone ?? '').replace(/\D/g, '');
}

/**
 * Do two numbers belong to the same phone?
 *
 * Compared on the last nine digits, so a local `077 123 4501` matches an E.164
 * `+94 77 123 4501`. Blank never matches blank — half the members have no
 * email and some have no phone of their own, and "both empty" is not a match.
 */
export function samePhone(a?: string | null, b?: string | null): boolean {
  const x = digitsOf(a);
  const y = digitsOf(b);
  if (x.length < 9 || y.length < 9) return false;
  return x.slice(-9) === y.slice(-9);
}

// ── Requests ────────────────────────────────────────────────────────────────

/** Σ items − credit. A credit note reduces what is owed; it is not a payment. */
export function requestTotal(r: PaymentRequest): number {
  const items = (r.items ?? []).reduce((s, i) => s + (Number(i.amountMinor) || 0), 0);
  return items - (r.creditMinor ?? 0);
}

export function requestPaid(r: PaymentRequest): number {
  return (r.payments ?? []).reduce((s, p) => s + (Number(p.amountMinor) || 0), 0);
}

/**
 * What is still owed, never negative. Overpayment is held as credit on the
 * payer (D52), so it does not show here as a negative balance.
 */
export function requestBalance(r: PaymentRequest): number {
  return Math.max(0, requestTotal(r) - requestPaid(r));
}

/**
 * Stored state wins; otherwise the status follows the money and the date.
 *
 * The order matters: paid before partly (a request paid in two instalments is
 * paid, not partly paid), and partly before overdue (someone who has paid
 * something is not in the overdue bucket the reminder run sweeps).
 */
export function requestStatus(r: PaymentRequest, today: string = todayISO()): RequestStatus {
  if (r.state === 'draft' || r.state === 'cancelled' || r.state === 'waiting') return r.state;
  const total = requestTotal(r);
  const paid = requestPaid(r);
  if (paid >= total) return 'paid';
  if (paid > 0) return 'partly';
  if (today > r.due) return 'overdue';
  return 'due';
}

/** Requests that count towards a member: drafts and cancellations do not. */
export function memberRequests(data: NetlyPayData, memberId: string): PaymentRequest[] {
  return (data.requests ?? []).filter(
    (r) => r.memberId === memberId && r.state !== 'draft' && r.state !== 'cancelled',
  );
}

// ── Members ─────────────────────────────────────────────────────────────────

/**
 * Stored state wins — a frozen member with an unpaid fee reads as Frozen, not
 * Overdue, because no request is being chased while they are frozen.
 */
export function memberStatus(data: NetlyPayData, member: Member, today: string = todayISO()): MemberStatus {
  if (member.state) return member.state;
  const open = memberRequests(data, member.id);
  if (open.some((r) => requestStatus(r, today) === 'overdue')) return 'overdue';
  return 'active';
}

export function memberBalance(data: NetlyPayData, member: Member): number {
  return memberRequests(data, member.id).reduce((s, r) => s + requestBalance(r), 0);
}

/** The most recent payment across all of a member's requests. */
export function lastPayment(data: NetlyPayData, member: Member): RequestPayment | null {
  let last: RequestPayment | null = null;
  memberRequests(data, member.id).forEach((r) =>
    (r.payments ?? []).forEach((p) => {
      if (!last || p.date > last.date) last = p;
    }),
  );
  return last;
}

export function batchOf(data: NetlyPayData, batchId: string): Batch | undefined {
  return (data.batches ?? []).find((b) => b.id === batchId);
}

export function programOf(data: NetlyPayData, batchId: string): Program | undefined {
  const b = batchOf(data, batchId);
  return b ? (data.programs ?? []).find((p) => p.id === b.programId) : undefined;
}

/**
 * Is this member being billed at all?
 *
 * Only the STORED state decides. Frozen, expired and pending members are left
 * out of batch and program billing: a frozen member is not training, and a
 * pending one has not been approved yet.
 *
 * A member who is merely overdue stays in — they owe money, so the next fee
 * still goes out, and stopping their billing would be the opposite of what an
 * owner wants. 'overdue' is derived and never stored, so `!state` covers it;
 * the prototype's extra `state === 'overdue'` check was unreachable.
 */
export function isAttending(member: Member): boolean {
  return !member.state;
}

/**
 * Who a request addressed to a member, a batch or a program actually reaches.
 *
 * Returns members, in the order they appear in the data, with the non-billable
 * ones removed. A caller showing "Send to N members" must count this, not the
 * roster — the two differ whenever anyone is frozen.
 */
export function recipientsFor(
  data: NetlyPayData,
  mode: 'member' | 'batch' | 'program',
  ids: string[],
): Member[] {
  const members = data.members ?? [];
  if (mode === 'member') {
    return ids.map((id) => members.find((m) => m.id === id)).filter((m): m is Member => !!m);
  }
  if (mode === 'batch') {
    return members.filter((m) => ids.includes(m.batchId) && isAttending(m));
  }
  const batchIds = (data.batches ?? []).filter((b) => ids.includes(b.programId)).map((b) => b.id);
  return members.filter((m) => batchIds.includes(m.batchId) && isAttending(m));
}

/**
 * A member or guardian who looks like this one already.
 *
 * Checked on save, before anything is written: the same phone as a member or
 * one of their guardians, or the same name. Nothing is merged on this — it
 * opens the side-by-side warning (DESIGN-BRIEF.md §C), because a shared family
 * phone is normal and two players can share a name.
 */
export function findDuplicate(
  data: NetlyPayData,
  candidate: {name: string; phone: string},
  ignoreId?: string | null,
): Member | null {
  const name = candidate.name.trim().toLowerCase();
  const found = (data.members ?? []).find((m) => {
    if (ignoreId && m.id === ignoreId) return false;
    if (samePhone(m.phone, candidate.phone)) return true;
    if ((m.guardians ?? []).some((g) => samePhone(g.phone, candidate.phone))) return true;
    return !!name && m.name.trim().toLowerCase() === name;
  });
  return found ?? null;
}

/**
 * A POS customer who is probably this member, by phone (D57).
 *
 * Only customers not already linked to someone else, matched against the
 * member's own number or a guardian's. A suggestion, never a link.
 */
export function suggestedPosCustomer<T extends {id: string; phone: string; memberId?: string | null}>(
  customers: T[],
  member: Member,
): T | null {
  if (member.posCustomerId) return null;
  const found = (customers ?? []).find(
    (c) =>
      !c.memberId &&
      (samePhone(c.phone, member.phone) ||
        (member.guardians ?? []).some((g) => samePhone(g.phone, c.phone))),
  );
  return found ?? null;
}

// ── Attendance ──────────────────────────────────────────────────────────────

/**
 * (P + L) ÷ (P + L + A), as a percentage, or null when nothing was held.
 *
 * Excused sessions are left OUT of both sides, rather than counted as
 * attended: a member excused from half the term should not read as 100%, and
 * should not be punished either. Null means "no answer yet", which the
 * Attendance screen renders as a dash — not as 0%.
 */
export function attendanceRate(marks: (AttendanceMark | undefined | null)[]): number | null {
  const counted = marks.filter((m) => m === 'P' || m === 'L' || m === 'A');
  if (!counted.length) return null;
  const there = counted.filter((m) => m === 'P' || m === 'L').length;
  return Math.round((there / counted.length) * 100);
}

/** One member's marks for a batch, oldest first. Future sessions are skipped. */
export function marksFor(
  data: NetlyPayData,
  member: Member,
  today: string = todayISO(),
): {session: Session; mark?: AttendanceMark; held: boolean}[] {
  return (data.sessions ?? [])
    .filter((s) => s.batchId === member.batchId)
    .map((s) => ({
      session: s,
      mark: (data.attendance ?? {})[s.id]?.[member.id],
      held: s.date < today,
    }));
}

// ── Status chips ────────────────────────────────────────────────────────────

/**
 * The one chip system, from DESIGN-BRIEF.md.
 *
 * Every chip carries a dot SHAPE as well as a colour, so the states remain
 * distinguishable without colour — overdue is a square, due is a ring, partly
 * paid is a half-filled circle.
 */
export type ChipTone = 'success' | 'info' | 'danger' | 'warning' | 'accent' | 'neutral';
export type ChipDot = 'solid' | 'ring' | 'square' | 'half' | 'ring-dot' | 'hollow-square' | 'bar';

export interface Chip {
  label: string;
  tone: ChipTone;
  dot: ChipDot;
}

const REQUEST_CHIPS: Record<RequestStatus, Chip> = {
  paid: {label: 'Paid', tone: 'success', dot: 'solid'},
  due: {label: 'Due', tone: 'info', dot: 'ring'},
  overdue: {label: 'Overdue', tone: 'danger', dot: 'square'},
  partly: {label: 'Partly paid', tone: 'warning', dot: 'half'},
  waiting: {label: 'Waiting for approval', tone: 'accent', dot: 'ring-dot'},
  draft: {label: 'Draft', tone: 'neutral', dot: 'hollow-square'},
  cancelled: {label: 'Cancelled', tone: 'neutral', dot: 'solid'},
};

const MEMBER_CHIPS: Record<MemberStatus, Chip> = {
  active: {label: 'Active', tone: 'success', dot: 'solid'},
  overdue: {label: 'Overdue', tone: 'danger', dot: 'square'},
  frozen: {label: 'Frozen', tone: 'info', dot: 'square'},
  expired: {label: 'Expired', tone: 'neutral', dot: 'bar'},
  pending: {label: 'Pending approval', tone: 'warning', dot: 'ring'},
};

export function requestChip(status: RequestStatus): Chip {
  return REQUEST_CHIPS[status];
}

export function memberChip(status: MemberStatus): Chip {
  return MEMBER_CHIPS[status];
}

/** Tailwind classes per tone, from the dashboard's own tokens. */
export const CHIP_CLASSES: Record<ChipTone, string> = {
  success: 'bg-pos-soft text-pos',
  info: 'bg-info-soft text-info',
  danger: 'bg-neg-soft text-neg',
  warning: 'bg-warn-soft text-warn',
  accent: 'bg-brand-soft text-brand',
  neutral: 'bg-surface3 text-text2',
};
